/**
 * Sign-out flow (task 7.6, public-website spec).
 *
 * Implements **Requirement 5.8**: an authenticated user clicks
 * "Sign out" on the AppShell, the SPA invokes the Cognito Hosted UI
 * sign-out endpoint, clears `sessionStorage`, dispatches
 * `authSignedOut`, and navigates to `/`.
 *
 * Two orderings would satisfy the requirement; this module picks the
 * **navigate-then-redirect-to-Hosted-UI** variant per the task brief
 * (the "simpler version" called out under §3 "Sign-out implementation
 * flow"). The local React state (auth slice + sessionStorage tokens
 * + the in-app route) is reset synchronously so any render that
 * happens between the dispatch and the Hosted UI redirect already
 * shows the unauthenticated chrome. The browser then leaves the SPA
 * for the Hosted UI's `/logout` endpoint, which itself redirects back
 * to the configured `logout_uri` (= the SPA origin) — by that point
 * the SPA is already in its unauthenticated state, so the redirect
 * lands on `/` with the chrome already coherent.
 *
 * Function-length budget: every export below stays under 20 lines
 * per Requirement 3.7. The Hosted UI URL builder is decomposed into
 * `buildHostedUiLogoutUrl` so `performSignOut` itself stays compact.
 */

import { authSignedOut } from '../../store/slices/auth-slice';
import { getCognitoConfig } from './cognito-config';
import { clearTokens } from './token-storage';

/**
 * Action-dispatch shape `performSignOut` accepts. Loose `unknown` so
 * the helper is testable without importing the full `AppDispatch`
 * type from the store (which would create a cycle: store imports
 * slices, slices import nothing, sign-out imports slice's action
 * creator only).
 */
export type SignOutDispatch = (action: { readonly type: string }) => unknown;

/** Navigation shape `performSignOut` accepts (matches `useNavigate`). */
export type SignOutNavigate = (path: string) => void;

/**
 * Build the Cognito Hosted UI `/logout` URL for the supplied app
 * client id and post-logout origin. The Hosted UI redirects back to
 * `logout_uri` once it has invalidated the user pool session.
 *
 * @param hostedUiUrl Origin of the Hosted UI from `cognito-config`.
 * @param userPoolClientId SPA app client id.
 * @param logoutUri Post-logout redirect target (= SPA origin + `/`).
 * @returns The fully-formed `${hostedUiUrl}/logout?...` URL.
 */
export function buildHostedUiLogoutUrl(
  hostedUiUrl: string,
  userPoolClientId: string,
  logoutUri: string,
): string {
  const params = new URLSearchParams({
    client_id: userPoolClientId,
    logout_uri: logoutUri,
  });
  return `${hostedUiUrl}/logout?${params.toString()}`;
}

/**
 * Arguments passed to {@link performSignOut} so the same function
 * works against the real Redux dispatch + react-router navigate, the
 * test-double dispatch + navigate from `tst/lib/auth/sign-out.test.ts`,
 * and any future caller that wires its own.
 */
export interface PerformSignOutArgs {
  /** Redux dispatch (typed loosely — see {@link SignOutDispatch}). */
  readonly dispatch: SignOutDispatch;
  /** react-router navigate (typed loosely — see {@link SignOutNavigate}). */
  readonly navigate: SignOutNavigate;
}

/**
 * Run the sign-out side effects per **Requirement 5.8**:
 *
 *   1. Clear the sessionStorage token record (idempotent).
 *   2. Dispatch `authSignedOut` to reset the auth slice.
 *   3. Navigate to `/` (in-app, before the Hosted UI redirect lands).
 *   4. Redirect the whole tab to the Hosted UI `/logout` endpoint
 *      when the Cognito config carries real values; the Hosted UI
 *      will redirect back to `logout_uri` (= the SPA origin) after
 *      invalidating the user pool session. When the config is still
 *      placeholder strings (the `core-infrastructure` follow-up has
 *      not landed) the Hosted UI redirect is skipped — the in-app
 *      reset is still complete.
 *
 * @param args Dispatch + navigate. See {@link PerformSignOutArgs}.
 */
export function performSignOut(args: PerformSignOutArgs): void {
  const { dispatch, navigate } = args;
  const { userPoolClientId, hostedUiUrl } = getCognitoConfig();
  clearTokens(userPoolClientId);
  dispatch(authSignedOut());
  navigate('/');
  if (hostedUiUrl && userPoolClientId) {
    const logoutUri = `${window.location.origin}/`;
    window.location.assign(
      buildHostedUiLogoutUrl(hostedUiUrl, userPoolClientId, logoutUri),
    );
  }
}
